/**
*
*   Generic form
*       - A Generic, Customizable Form
*
**/
'use client';

import Button from '@/components/button';
import Recaptcha from '@/libraries/recaptcha';
import { AnalyticTools } from '@/libraries/analytics';
import { useSearchParams } from 'next/navigation';
import { PatternFormat } from 'react-number-format';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslations } from 'next-intl';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useState } from 'react';


//Build the Element
export default function GenericForm({ type = 'general', data = {} }){

	const { control, register, setError, formState: { errors, isSubmitting, isSubmitSuccessful }, handleSubmit } = useForm();

	const [ status , setStatus ] = useState(false);

	const [ loading , setLoading ] = useState(false);

	const t = useTranslations();

	//Get the Search Params
	const searchParams = useSearchParams();

	//Recaptcha
	const { executeRecaptcha } = useReCaptcha();

	//Override the Messages
	data = Object.assign({

		fields: [{
			label: t('Forms.Labels.name'),
			error: t('Forms.Labels.name'),
			type:  'text'
		},{
			label: t('Forms.Labels.company'),
			error: t('Forms.Labels.company'),
			type:  'company'
		},{
			label: t('Forms.Labels.email'),
			error: t('Forms.Labels.email'),
			type:  'email'
		},{
			label: t('Forms.Labels.phone'),
			error: t('Forms.Labels.phone'),
			type:  'phone'
		},{
			label: t('Forms.Labels.message'),
			error: t('Forms.Labels.message'),
			type:  'textarea'
		}],

		//Name
		name: 'Inquiry',

		//The Primary Field Name
		primary: t('Forms.Labels.name'),

		//The Subject Criteria Fallback (check for each until one is not null)
		subject: [ t('Forms.Labels.company'), t('Forms.Labels.name') ],

		//Success Message
		success: 'Your Inquiry has been sent!',

		//Submit Button
		submit: t('Forms.Labels.submit')

	},data);

	//Override the Fields

	//Form Settings
	const Form = {

		//Settings
		email: {
			validate: email => {
				return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email) || t('Forms.Errors.email.invalid'); //eslint-disable-line no-useless-escape
			}
		},

		//The Placeholder
		placeholder: field => field.label + ( field.error ? '*' : '' ),

		//Return the formatted required error field if one exists
		required: field => {
			if( field.error ) return t('Forms.Errors.required',{ type: field.error });
			return false;
		},

		//Error
		error: ({ message }) => <p className="error">{ message }</p> ,

		//A Thrown Error Message
		thrown: ( message ) => setError('root.serverError',{
			type: 500,
			message: `${ t('Forms.Errors.unknown')  } ${  message }`
		}),

		//On Submit
		submit: async submission => {

			setLoading(true);

			//Get the Token
			const token = await executeRecaptcha('generic/email');

			//Readable Form Type
			const name = ( type[0].toUpperCase() + type.slice(1).toLowerCase() );

			fetch( '/api/email' , {
				method: 'POST',
				body:   JSON.stringify({
					token:      token,
					to:         'sales@fernandpetal.com',
					template:   'default',
					subject:    ((output)=>{
						if( data.subject ){

							//Find the first subject that works
							if( Array.isArray( data.subject ) )
								data.subject = data.subject.find( item => !!submission[ item ] );

							//Get the Output
							output = `${ type  }: ${  submission[ data.subject ] }`;

						}
						return output;
					})(type),
					body:       {
						title:  `${ type  }`,
						data:   submission
					}
				})
			})
			//Format to JSON
			.then( res => res.json() )

			//Success
			.then( response => {

				//Set Success or Failure
				setStatus( response.result );

				//An Error Occured
				if(!response.result )
					Form.thrown( JSON.stringify( response.error ) );

				//Track Analytics
				if( response.result )
					AnalyticTools.inquiry( 100 , type );

				//Stop Loading
				setLoading(false);

			})

			//Catch Error
			.catch( err => {
				setLoading(false);
				Form.thrown( err.toString() );
			});
		}
	};

	return (
		<form onSubmit={ handleSubmit(Form.submit) }>{
			status ? (
				<div className="success">{
					data.success
				}</div>
			) : (
				<>
					{/* Submission Response Error */}
					{ errors.root?.serverError?.type > 200 && (
						<div className="failed">{
							errors.root.serverError.message
						}</div>
					) }

					{
						data.fields.map( ( group , i ) => (
							<div key={i} className="row">{
								( !Array.isArray(group) ? [ group ] : group ).map( ( field , j ) => (
									<div key={j}>
										<label htmlFor={ field.label }>{ field.label }</label>
										{
											(()=>{
												switch( field.type ){

													//Phone
													case 'phone': return (
														<Controller control={control} name={ field.label } render={({ field: { onChange, name, value } }) => (
															<PatternFormat format="(###) ###-####" name={name} value={value} onChange={onChange} placeholder={ Form.placeholder( field ) } />
														)} />
													);

													//Email
													case 'email': return (
														<input name={ field.label } placeholder={ Form.placeholder( field ) } {...register( field.label, { required: Form.required( field ), validate: Form.email.validate })} />
													);

													case 'select': return (
														<select name={ field.label } {...register( field.label, { required: Form.required( field ) })}>{
															field.options.map( ( option , j ) => (
																<option key={j} value={ option }>{ option }</option>
															))
														}</select>
													);

													case 'textarea': return (
														<textarea name={ field.label } placeholder={ Form.placeholder( field ) } {...register( field.label, { required: Form.required( field ) })} />
													);

													//Default Input Field
													default: return (
														<input name={ field.label } placeholder={ Form.placeholder( field ) } { ...register( field.label , { required: Form.required( field ) } ) } />
													);

												}
											})()
										}
										<ErrorMessage errors={errors} name={ field.label } render={ Form.error } />
									</div>
								))
							}</div>
						))
					}

					{/* Recaptcha Message */}
					<p>{ Recaptcha.message }</p>

					{/* Submit */}
					<div className="row">
						<div>
							<Button disabled={ isSubmitting || loading } button type="submit">{ data.submit }</Button>
							<input
								type="hidden"
								{...register('campaign',{
									required: false ,
									value: searchParams.get('utm_source')
								})}
							/>
						</div>
					</div>
				</>
			)
		}</form>
	);

}